import { create } from "zustand";
import type { NodePosition } from "./graph-types";

export interface DragState {
  activeNodeIds: string[];
  startPosition: NodePosition;
  delta: NodePosition;
}

export interface MeasureState {
  start: NodePosition;
  current: NodePosition;
}

export interface DrawState {
  start: NodePosition;
  current: NodePosition;
}

interface InteractionState {
  selection: string[];
  hovered: string | null;
  dragState: DragState | null;
  measureState: MeasureState | null;
  drawState: DrawState | null;

  setHovered: (id: string | null) => void;
  toggleSelection: (id: string) => void;
  selectExclusive: (id: string) => void;
  setSelection: (ids: string[]) => void;
  clearSelection: () => void;
  startDrag: (ids: string[], startPosition: NodePosition) => void;
  updateDrag: (delta: NodePosition) => void;
  endDrag: () => void;
  startMeasure: (start: NodePosition) => void;
  updateMeasure: (current: NodePosition) => void;
  endMeasure: () => void;
  startDraw: (start: NodePosition) => void;
  updateDraw: (current: NodePosition) => void;
  endDraw: () => void;
}

export const useInteractionStore = create<InteractionState>((set) => ({
  selection: [],
  hovered: null,
  dragState: null,
  measureState: null,
  drawState: null,

  setHovered: (id) => set({ hovered: id }),
  toggleSelection: (id) =>
    set((state) => ({
      selection: state.selection.includes(id)
        ? state.selection.filter((s) => s !== id)
        : [...state.selection, id],
    })),
  selectExclusive: (id) => set({ selection: [id] }),
  setSelection: (ids) => set({ selection: ids }),
  clearSelection: () => set({ selection: [] }),
  startDrag: (ids, startPosition) =>
    set({
      dragState: {
        activeNodeIds: ids,
        startPosition,
        delta: { x: 0, y: 0, z: 0 },
      },
    }),
  updateDrag: (delta) =>
    set((state) =>
      state.dragState ? { dragState: { ...state.dragState, delta } } : state,
    ),
  endDrag: () => set({ dragState: null }),
  startMeasure: (start) => set({ measureState: { start, current: start } }),
  updateMeasure: (current) =>
    set((state) =>
      state.measureState
        ? { measureState: { ...state.measureState, current } }
        : state,
    ),
  endMeasure: () => set({ measureState: null }),
  startDraw: (start) => set({ drawState: { start, current: start } }),
  updateDraw: (current) =>
    set((state) =>
      state.drawState ? { drawState: { ...state.drawState, current } } : state,
    ),
  endDraw: () => set({ drawState: null }),
}));
